import { ReactComponent as TelegramIcon } from '@/assets/images/telegram.svg';
import { ReactComponent as WebsiteIcon } from '@/assets/images/website.svg';
import { ReactComponent as XIcon } from '@/assets/images/x.svg';
import config from '@/common-helpers/web3modalconfig';
import ConnectWalletButton from '@/components/ConnectWalletButton';
import {
    APP_TOKEN,
    DATA_REGISTRY_CONTRACT_ADDRESS,
    DATA_VERIFIED_COMPUTING_CONTRACT_ADDRESS,
    ENCRYPTION_SEED,
} from '@/constant';
import DATA_REGISTRY_CONTRACT_ABI from '@/constant/abi/data-registry.json';
import DATA_VERIFIED_COMPUTING_CONTRACT_ABI from '@/constant/abi/data-verified-computing.json';
import { getServerApi } from '@/gql';
import { useGetUserTaskTemplateUse } from '@/hooks/useGetUserTaskTemplateUse';
import user from '@/stores/user';
import Alert from '@/utils/components/alert';
import { encrypt } from '@/utils/crypto';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    CircularProgress,
    OutlinedInput,
} from '@mui/material';
import {
    readContract,
    signMessage,
    waitForTransactionReceipt,
    writeContract,
} from '@wagmi/core';
import axios from 'axios';
import * as crypto from 'crypto';
import { observer } from 'mobx-react';
import NodeRSA from 'node-rsa';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingModal from './LoadingModal';
import { Step } from './Step';

let ipfsFileId = '';

function Home() {
    const [formData, setFormData] = useState({
        question: '',
        answer: '',
    });
    const [errors, setErrors] = useState({
        question: '',
        answer: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [password, setPassword] = useState('');
    const [ipfsUrl, setIpfsUrl] = useState('');
    const [uploadComplete, setUploadComplete] = useState(false);
    const [fileId, setFileId] = useState<bigint | null>(null);
    const [jobId, setJobId] = useState<bigint | null>(null);
    const [nodeAddress, setNodeAddress] = useState('');
    const [datGenerated, setDatGenerated] = useState(false);
    const [open, setOpen] = useState(false);
    const [progress, setProgress] = useState(0);
    const [canGenerateDAT, setCanGenerateDAT] = useState(false);
    const { t } = useTranslation();
    const { connected, address } = user;

    const { userTaskTemplateUse, refetch: refetchUserTaskRecords } =
        useGetUserTaskTemplateUse();

    useEffect(() => {
        if (userTaskTemplateUse) {
            const template = userTaskTemplateUse.find(
                (template) => template.taskTemplateId === 'contributeCALITHDat',
            );
            const finishCount = Number(template?.taskFinishCount ?? 0);
            if (finishCount < Number(template?.content?.dailyTimesLimit ?? 0)) {
                setCanGenerateDAT(true);
            } else {
                setCanGenerateDAT(false);
            }
        }
    }, [userTaskTemplateUse]);

    const toggleDialog = (isOpen: boolean) => {
        setOpen(isOpen);
    };

    const validateForm = () => {
        const newErrors = {
            question: '',
            answer: '',
        };

        if (!formData.question.trim()) {
            newErrors.question = 'Question is required';
        }

        if (!formData.answer.trim()) {
            newErrors.answer = 'Answer is required';
        }

        setErrors(newErrors);
        return !newErrors.question && !newErrors.answer;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
            ...prev,
            [event.target.name]: event.target.value,
        }));
        if (errors[event.target.name as keyof typeof errors]) {
            setErrors((prev) => ({
                ...prev,
                [event.target.name]: '',
            }));
        }
    };

    const uint8ArrayToBase64 = (array: Uint8Array): string => {
        let result = '';
        const chunkSize = 1024;
        for (let i = 0; i < array.length; i += chunkSize) {
            const chunk = array.subarray(i, i + chunkSize);
            result += btoa(String.fromCharCode.apply(null, Array.from(chunk)));
        }
        return result;
    };

    const calculateSHA256 = (text: string): string => {
        return crypto.createHash('sha256').update(text, 'utf8').digest('hex');
    };

    const encryptAndUploadData = async (): Promise<{
        url: string;
        password: `0x${string}`;
    }> => {
        try {
            //Generating encryption key...
            const password = await signMessage(config, {
                message: ENCRYPTION_SEED,
            });
            setPassword(password);

            //Encrypting your data...
            const encryptedData = await encrypt(
                Uint8Array.from(formData.answer),
                password,
            );

            //Uploading to IPFS...
            const uploadResponse = await axios.post(
                `${import.meta.env.VITE_UPLOAD_URL}/uploadPinata`,
                {
                    fileData: uint8ArrayToBase64(encryptedData),
                    question: formData.question,
                    typeStr: 'text/plain',
                },
            );
            if (!uploadResponse?.data?.cid) {
                throw Error('Failed to upload data to IPFS');
            }
            //Finalizing upload...
            const url = `${import.meta.env.VITE_GATEWAY_URL}/ipfs/${uploadResponse.data.cid}?download=true`;
            setIpfsUrl(url);
            setUploadComplete(true);
            ipfsFileId = uploadResponse.data.id;

            return {
                url,
                password,
            };
        } catch (error) {
            setUploadComplete(false);
            console.error(
                `Error encrypting and uploading data: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
            );
            throw Error(
                `Error encrypting and uploading data: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
            );
        }
    };

    const claimPoints = async () => {
        const api = getServerApi();
        try {
            const claimResponse = await api.claim({
                req: {
                    app: APP_TOKEN,
                    templateCode: 'contributeCALITHDat',
                },
            });

            if (claimResponse?.claim?.data) {
                setCanGenerateDAT(false);
                refetchUserTaskRecords();
            } else {
                console.error('Claim points failed');
            }
        } catch (error) {
            console.error('Claim points failed:', error);
        }
    };

    const generateDAT = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isSubmitting) {
            setOpen(true);
            return;
        }
        try {
            if (!validateForm()) {
                throw Error('Please enter both question and answer.');
            }
            setIsSubmitting(true);
            toggleDialog(true);

            const { url, password } = await encryptAndUploadData();
            setProgress(10);

            //Registering file on blockchain...
            let fileId = (await readContract(config, {
                address: DATA_REGISTRY_CONTRACT_ADDRESS,
                abi: DATA_REGISTRY_CONTRACT_ABI,
                functionName: 'getFileIdByUrl',
                args: [url],
            })) as bigint;
            setProgress(20);
            if (fileId === BigInt(0)) {
                const privacyDataSha256 = calculateSHA256(formData.answer);
                const addFileTx = await writeContract(config, {
                    address: DATA_REGISTRY_CONTRACT_ADDRESS,
                    abi: DATA_REGISTRY_CONTRACT_ABI,
                    functionName: 'addFile',
                    args: [url, privacyDataSha256],
                });
                const addFileReceipt = await waitForTransactionReceipt(config, {
                    hash: addFileTx,
                });
                if (addFileReceipt.status !== 'success') {
                    throw Error('Add file transaction failed');
                }
                setProgress(30);

                // Now query the fileId after transaction is confirmed
                fileId = (await readContract(config, {
                    address: DATA_REGISTRY_CONTRACT_ADDRESS,
                    abi: DATA_REGISTRY_CONTRACT_ABI,
                    functionName: 'getFileIdByUrl',
                    args: [url],
                })) as bigint;
            }
            setFileId(fileId);
            setProgress(40);

            //Requesting verification proof...
            const requestProofTx = await writeContract(config, {
                address: DATA_VERIFIED_COMPUTING_CONTRACT_ADDRESS,
                abi: DATA_VERIFIED_COMPUTING_CONTRACT_ABI,
                functionName: 'requestProof',
                args: [fileId],
                value: 100,
            });
            const requestProofReceipt = await waitForTransactionReceipt(
                config,
                {
                    hash: requestProofTx,
                },
            );
            if (requestProofReceipt.status !== 'success') {
                throw Error('Request proof transaction failed');
            }
            setProgress(50);
            const jobIds = (await readContract(config, {
                address: DATA_VERIFIED_COMPUTING_CONTRACT_ADDRESS,
                abi: DATA_VERIFIED_COMPUTING_CONTRACT_ABI,
                functionName: 'fileJobIds',
                args: [fileId],
            })) as bigint[];
            setProgress(60);
            const jobId = jobIds[jobIds.length - 1];
            const job: any = await readContract(config, {
                address: DATA_VERIFIED_COMPUTING_CONTRACT_ADDRESS,
                abi: DATA_VERIFIED_COMPUTING_CONTRACT_ABI,
                functionName: 'getJob',
                args: [jobId],
            });
            setJobId(jobId);
            setProgress(70);

            //Processing verification...
            const nodeInfo: any = await readContract(config, {
                address: DATA_VERIFIED_COMPUTING_CONTRACT_ADDRESS,
                abi: DATA_VERIFIED_COMPUTING_CONTRACT_ABI,
                functionName: 'getNode',
                args: [job.nodeAddress],
            });
            setProgress(80);
            setNodeAddress(job.nodeAddress);

            //Request Proof from the verified computing node...
            const nodeUrl = nodeInfo.url;
            const pubKey = nodeInfo.publicKey;
            const rsa = new NodeRSA(pubKey, 'pkcs1-public-pem');
            const encryptedKey = rsa.encrypt(password, 'hex');
            const response = await axios.post(
                `${import.meta.env.VITE_PROXY_URL}/proxy/postAgent?url=${nodeUrl}/proof`,
                {
                    job_id: Number(jobId),
                    file_id: Number(fileId),
                    file_url: url,
                    encryption_key: encryptedKey,
                    encryption_seed: ENCRYPTION_SEED,
                    nonce: null,
                    proof_url: null,
                },
            );
            if (response.data.code !== 200) {
                throw Error(
                    'Failed to send proof request: ' + JSON.stringify(response),
                );
            }
            setProgress(90);

            //Generating DAT...
            const requestRewardTx = await writeContract(config, {
                address: DATA_REGISTRY_CONTRACT_ADDRESS,
                abi: DATA_REGISTRY_CONTRACT_ABI,
                functionName: 'requestReward',
                args: [fileId, 1],
            });
            const requestRewardReceipt = await waitForTransactionReceipt(
                config,
                {
                    hash: requestRewardTx,
                },
            );
            if (requestRewardReceipt.status !== 'success') {
                throw Error('Generate DAT transaction failed');
            }
            ipfsFileId = '';
            claimPoints();
            setProgress(100);
            setCanGenerateDAT(false);
            setDatGenerated(true);
            setIsSubmitting(false);
            setOpen(false);
            setFormData({ question: '', answer: '' });
            Alert({
                type: 'success',
                text: 'DAT generated successfully!',
            });
        } catch (error) {
            axios
                .post(`${import.meta.env.VITE_UPLOAD_URL}/deletePinata`, {
                    files: [ipfsFileId],
                })
                .finally(() => (ipfsFileId = ''));
            Alert({
                type: 'error',
                text: `Error generating DAT: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
            });
            console.error('Error generating DAT:', error);
            setIsSubmitting(false);
            setProgress(0);
            setOpen(false);
        }
    };

    return (
        <div className="mx-auto mt-[60px] w-[1060px] overflow-hidden max-xl:w-full max-xl:px-[20px]">
            <div className="text-center text-[32px] font-bold">
                {t('title')}
            </div>
            <div className="mb-[40px] mt-[10px] text-center font-normal">
                {t('description')}
            </div>
            <div className="grid grid-cols-2 items-center gap-[50px] max-md:grid-cols-1">
                <div className="flex shrink-0 flex-col gap-[24px] rounded-[25px] bg-white p-[40px] shadow-[0px_10px_20px_0px_#0000001a] max-md:gap-[16px] max-md:p-[20px]">
                    <div className="px-[40px] text-center text-[24px] font-bold max-md:px-[20px]">
                        Steps to Contribute Data & Generate DAT
                    </div>
                    <Step
                        index={1}
                        title="Connect your Wallet:"
                        description="Use MetaMask. Same wallet as Corrupted Alith = Points summed up"
                        image={require('@/assets/images/step-1.svg')}
                    />
                    <Step
                        index={2}
                        title="Claim Testnet Tokens:"
                        description={
                            <div>
                                Click{' '}
                                <a
                                    href="https://t.me/lazai_testnet_bot"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary"
                                >
                                    the faucet link
                                </a>{' '}
                                to get tokens for transaction fees.
                            </div>
                        }
                        image={require('@/assets/images/step-2.svg')}
                    />
                    <Step
                        index={3}
                        title="Input Answer & Question:"
                        description="Provide your data to correct C.Alith."
                        image={require('@/assets/images/step-3.svg')}
                    />
                    <Step
                        index={4}
                        title="Submit and Generate:"
                        description='Click the "Submit & Generate" button.'
                        image={require('@/assets/images/step-4.svg')}
                    />
                    <Step
                        index={5}
                        title="Sign the Message:"
                        description="Approve the transaction in Metamask."
                        image={require('@/assets/images/step-5.svg')}
                    />
                    <Step
                        index={6}
                        title="Receive your DAT:"
                        description="The DAT will be sent to your wallet."
                        image={require('@/assets/images/step-6.svg')}
                    />
                </div>
                <div className="flex shrink-0 flex-col gap-[30px]">
                    <div className="text-center text-[24px] font-bold">
                        Contribute Your Data
                    </div>
                    <form
                        onSubmit={generateDAT}
                        className="flex flex-col gap-[30px]"
                    >
                        <div className="relative">
                            <div className="mb-[5px]">Question</div>
                            <OutlinedInput
                                name="question"
                                multiline
                                rows={1}
                                placeholder="Enter your question here..."
                                value={formData.question}
                                onChange={handleInputChange}
                                disabled={isSubmitting}
                                classes={{
                                    root: `!bg-white w-full rounded-[12px] text-[14px] shadow-[0px_10px_20px_0px_#0000001a] ${errors.question ? '!border-red-500' : ''}`,
                                    multiline: 'p-[20px]',
                                    notchedOutline: '!border-none',
                                }}
                            />
                            {errors.question && (
                                <div className="absolute -bottom-[23px] left-[20px] text-[12px] text-red-500">
                                    {errors.question}
                                </div>
                            )}
                        </div>
                        <div className="relative">
                            <div className="mb-[5px]">Answer</div>
                            <OutlinedInput
                                name="answer"
                                multiline
                                rows={4}
                                placeholder="Enter your answer here..."
                                value={formData.answer}
                                onChange={handleInputChange}
                                disabled={isSubmitting}
                                classes={{
                                    root: `!bg-white w-full rounded-[12px] text-[14px] shadow-[0px_10px_20px_0px_#0000001a] ${errors.answer ? '!border-red-500' : ''}`,
                                    multiline: 'p-[20px]',
                                    notchedOutline: '!border-none',
                                }}
                            />
                            {errors.answer && (
                                <div className="absolute -bottom-[23px] left-[20px] text-[12px] text-red-500">
                                    {errors.answer}
                                </div>
                            )}
                        </div>
                        {address && (
                            <Button
                                type="submit"
                                disabled={!canGenerateDAT}
                                className="m-auto h-[50px] w-[185px] rounded-[20px] bg-primary text-[14px] font-normal normal-case text-white hover:bg-primaryFocus hover:text-secondary disabled:bg-disabled disabled:text-disabledForeground"
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center gap-2">
                                        <CircularProgress
                                            size={16}
                                            color="inherit"
                                        />
                                        Submitting...
                                    </div>
                                ) : canGenerateDAT ? (
                                    'Submit & Generate'
                                ) : (
                                    'Submit Tomorrow'
                                )}
                            </Button>
                        )}
                    </form>
                    {!address && (
                        <ConnectWalletButton className="m-auto !h-[50px] !w-[185px] !rounded-[20px]" />
                    )}
                </div>
            </div>
            <div className="mt-[40px] grid grid-cols-3 items-start gap-[10px] max-md:grid-cols-1">
                <Accordion className="!m-0 !rounded-[12px] border border-primary/40 shadow-none before:hidden">
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        classes={{
                            root: '!h-[60px] !min-h-[60px]',
                        }}
                    >
                        <div className="text-[20px] font-bold">
                            Why Contribute Data?
                        </div>
                    </AccordionSummary>
                    <AccordionDetails className="text-[14px]">
                        <ol className="list-decimal pl-5 [&>li::marker]:font-bold">
                            <li>
                                <div>
                                    <span className="font-bold">
                                        Correct C.Alith:
                                    </span>{' '}
                                    Help improve C.Alith's dataset and
                                    functionality.
                                </div>
                            </li>
                            <li>
                                <div>
                                    <span className="font-bold">
                                        Earn Rewards:
                                    </span>{' '}
                                    Each DAT generation earns you 300 Calith
                                    Points.
                                </div>
                            </li>
                        </ol>
                    </AccordionDetails>
                </Accordion>
                <Accordion className="!m-0 !rounded-[12px] border border-primary/40 shadow-none before:hidden">
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        classes={{
                            root: '!h-[60px] !min-h-[60px]',
                        }}
                    >
                        <div className="text-[20px] font-bold">
                            What is DAT?
                        </div>
                    </AccordionSummary>
                    <AccordionDetails className="text-[14px]">
                        <div>
                            The Data Anchoring Token (DAT) is a semi-fungible
                            token (SFT) standard designed for AI dataset
                            ownership, licensing, model anchoring, and
                            provenance tracking. Unlike traditional token
                            standards, DAT introduces a multi-layered ownership
                            model, supporting:
                        </div>
                        <div className="m-[16px]">
                            <div>a. Programmable AI data governance</div>
                            <div>b. Composable datasets</div>
                            <div>c. Decentralized AI model execution</div>
                        </div>
                        <div>
                            The core distinction of DAT is its native AI asset
                            integration, allowing structured access control,
                            automated licensing, and composable AI dataset
                            evolution.
                        </div>
                    </AccordionDetails>
                </Accordion>
                <Accordion className="!m-0 !rounded-[12px] border border-primary/40 shadow-none before:hidden">
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        classes={{
                            root: '!h-[60px] !min-h-[60px]',
                        }}
                    >
                        <div className="text-[20px] font-bold">
                            What is LazAI ?
                        </div>
                    </AccordionSummary>
                    <AccordionDetails className="text-[14px]">
                        LazAI is a Web3-native AI network redefining data for
                        the AI era — making it verifiable, ownable, and
                        composable to ensure human-aligned AI evolution.
                    </AccordionDetails>
                </Accordion>
            </div>
            <div className="mt-[50px] text-center text-[24px] font-bold">
                Follow us on
            </div>
            <div className="mt-[10px] flex items-center justify-center gap-[20px]">
                <Button
                    className="h-[50px] w-[60px] rounded-[15px] bg-primary text-white hover:bg-primaryFocus hover:text-secondary"
                    onClick={() =>
                        window.open('https://lazai.network/', '_blank')
                    }
                >
                    <WebsiteIcon />
                </Button>
                <Button
                    className="h-[50px] w-[60px] rounded-[15px] bg-primary text-white hover:bg-primaryFocus hover:text-secondary"
                    onClick={() =>
                        window.open('https://x.com/lazainetwork?s=11', '_blank')
                    }
                >
                    <XIcon />
                </Button>
                <Button
                    className="h-[50px] w-[60px] rounded-[15px] bg-primary text-white hover:bg-primaryFocus hover:text-secondary"
                    onClick={() =>
                        window.open('https://t.me/lazai_global', '_blank')
                    }
                >
                    <TelegramIcon />
                </Button>
            </div>
            <LoadingModal
                progress={progress}
                open={open}
                onClose={() => toggleDialog(false)}
            />
        </div>
    );
}
export default observer(Home);
